import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  ChevronLeft,
  Download,
  Share2,
  Printer,
  Maximize2,
  ShieldCheck,
  FileText,
  File as FileIcon,
  Info,
  ExternalLink } from
'lucide-react';
import { toast } from 'sonner';

const PowerPointSlide = ({ material }) => {
  const { t } = useTranslation('marketing_hub');
  return (
    <div className="w-full h-full bg-[#1A1A1A] text-white p-12 flex flex-col relative overflow-hidden font-sans">
            {/* Background Texture */}
            <div className="absolute inset-0 opacity-10 pointer-events-none"
      style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #ffffff10 1px, transparent 0)', backgroundSize: '24px 24px' }} />

            <div className="relative z-10 flex-1 flex flex-col">
                <h2 className="text-3xl font-light tracking-tight mb-12 text-[#E5D5C5] font-['Space_Grotesk']">
                    {t('preview.operational_intel')}
                </h2>

                <div className="flex gap-12 flex-1 items-center">
                    {/* Dashboard Mockup Area */}
                    <div className="flex-1 aspect-[4/3] bg-[#2A2A2A]/50 rounded-lg border border-white/5 p-6 relative group overflow-hidden shadow-2xl">
                        <div className="absolute top-2 left-4 text-[10px] font-mono text-white/20">{t("dashboardv4", "DASHBOARD_V4")}</div>
                        <div className="grid grid-cols-3 gap-4 h-full">
                            {[...Array(6)].map((_, i) =>
                              <div key={i} className="bg-[#1F1F1F] rounded-md border border-white/5 p-3 flex flex-col gap-2">
                                    <div className="h-2 w-1/2 bg-white/10 rounded" />
                                    <div className="flex-1 flex items-end gap-1">
                                        {[...Array(5)].map((_, j) =>
                                          <div key={j} className="flex-1 bg-teal-500/20 rounded-t-sm transition-all duration-700 group-hover:bg-teal-500/40"
                                          style={{ height: `${Math.random() * 60 + 20}%` }} />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content Column */}
                    <div className="w-1/3 space-y-6">
                        <div className="space-y-3">
                            <h3 className="text-xl font-medium text-[#E5D5C5] leading-snug">
                                {t('preview.reporting_title')}
                            </h3>
                            <p className="text-sm text-white/60 leading-relaxed font-light">
                                {t('preview.reporting_desc')}
                            </p>
                        </div>

                        <ul className="space-y-4 pt-4">
                            {(t('preview.bullets', { returnObjects: true }) || []).map((item, idx) =>
                              <li key={idx} className="flex items-start gap-3 text-sm font-light text-white/80">
                                    <div className="mt-1.5 h-1 w-4 bg-[#E5D5C5] shrink-0" />
                                    {item}
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-auto pt-8 flex justify-between items-end border-t border-white/5">
                    <div className="text-[10px] font-mono text-white/20 uppercase tracking-[0.2em]">{t('preview.overview_footer')}</div>
                    <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10">
                        <span className="text-[8px] font-bold tracking-widest text-white/40 uppercase">{t('preview.powered_by')}</span>
                        <span className="text-[10px] font-black italic text-[#E5D5C5] tracking-tight">{t("gamma", "GAMMA")}</span>
                    </div>
                </div>
            </div>
        </div>
  );
};

export const DocumentPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('marketing_hub');
  const [material, setMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(4); // Start on slide 4 as per screenshot
  const [totalPages, setTotalPages] = useState(10);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const lang = queryParams.get('lang') || 'bilingual';

    if (id && id.startsWith('BR-B2B-')) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      fetch(`${backendUrl}/api/crm/marketing-hub/contracts/${id}/preview?lang=${lang}`, {
          headers: {
              'Authorization': `Bearer ${localStorage.getItem('ezerpToken') || ''}`
          }
      })
      .then(res => {
          if (!res.ok) throw new Error('Contract template not found');
          return res.json();
      })
      .then(data => {
          setMaterial({
              id: data.code,
              title: `${data.code} - ${data.filename.replace(/\.[^/.]+$/, "")}`,
              description: `B2B Contract Template - ${data.code}. Under jurisdiction context: ${lang}.`,
              file_type: 'markdown',
              category: 'contracts',
              owner: 'Legal Department',
              updated_at: '2026-06-01',
              tags: ['B2B', 'Legal', 'Contract'],
              content: data.content,
              file_url: `${backendUrl}/api/crm/marketing-hub/contracts/${data.code}/download?lang=${lang}`
          });
          setTotalPages(1);
          setCurrentPage(1);
          setLoading(false);
      })
      .catch(err => {
          console.error("Error loading contract preview", err);
          setMaterial(null);
          setLoading(false);
      });
    } else {
      // Simulate loading from localStorage
      const saved = localStorage.getItem('marketing_hub_materials');
      if (saved) {
        try {
          const materials = JSON.parse(saved);
          const found = materials.find((m) => m.id.toString() === id);
          if (found) {
            setMaterial(found);
            if (found.file_type === 'powerpoint') {
              setTotalPages(12);
            }
          }
        } catch (e) {
          console.error("Failed to parse materials", e);
        }
      }
      const timer = setTimeout(() => {
        setLoading(false);
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [id]);

  const handleAction = (action) => {
    toast.info(`${action}: ${material?.title}`, {
      description: "Security protocols active. Feature available in protected environments."
    });
  };

  const handleDownload = () => {
    if (!material) return;
    if (material.id && material.id.startsWith('BR-B2B-')) {
      const queryParams = new URLSearchParams(window.location.search);
      const lang = queryParams.get('lang') || 'bilingual';
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      toast.promise(
        fetch(`${backendUrl}/api/crm/marketing-hub/contracts/${material.id}/download?lang=${lang}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('ezerpToken') || ''}`
            }
        })
        .then(res => {
            if (!res.ok) throw new Error('Download failed');
            return res.blob();
        })
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${material.id}_${lang}.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }),
        {
            loading: 'Fetching Word document...',
            success: 'Contract Word document downloaded successfully!',
            error: 'Failed to download contract document.'
        }
      );
    } else if (material.file_url) {
      window.open(material.file_url, '_blank');
      toast.success("Downloading document...");
    } else {
      handleAction('Downloading');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#050505]">
                <div className="flex flex-col items-center gap-6">
                    <div className="relative">
                        <div className="h-16 w-16 border-2 border-teal-500/20 rounded-full" />
                        <div className="absolute inset-0 h-16 w-16 border-2 border-teal-500 border-t-transparent rounded-full animate-spin shadow-[0_0_20px_rgba(20,184,166,0.2)]" />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                        <p className="font-['Space_Grotesk'] text-lg text-white font-bold tracking-tight">{t('preview.loading_vault')}</p>
                        <p className="text-[10px] font-mono text-teal-500/60 uppercase tracking-[0.3em] animate-pulse">{t('preview.decrypting')}</p>
                    </div>
                </div>
            </div>
    );
  }

  if (!material) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#050505]">
                <div className="text-center space-y-6 max-w-md p-10 bg-[#0A0A0A] rounded-2xl border border-white/5 shadow-2xl relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="bg-red-500/10 p-4 rounded-full w-fit mx-auto relative z-10 border border-red-500/20">
                        <Info className="h-10 w-10 text-red-500" />
                    </div>
                    <div className="space-y-2 relative z-10">
                        <h2 className="text-2xl font-bold text-white font-['Space_Grotesk'] tracking-tight">{t('preview.blocked_title')}</h2>
                        <p className="text-slate-400 text-sm leading-relaxed">{t('preview.blocked_desc')}</p>
                    </div>
                    <Button
            onClick={() => window.close()}
            className="w-full bg-white text-black hover:bg-slate-200 font-bold h-11 rounded-xl relative z-10">
                        {t('preview.terminate')}
                    </Button>
                </div>
            </div>
    );
  }

  const isPowerPoint = material.file_type === 'powerpoint';
  const isPresentation = material.title.toLowerCase().includes('presentation');

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col font-sans text-slate-200 overflow-hidden">
            {/* Precision Header */}
            <header className="h-16 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-3xl flex items-center justify-between px-6 sticky top-0 z-50 shadow-sm">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 pr-4 border-r border-white/10">
                        <ShieldCheck className="h-5 w-5 text-teal-500" />
                        <span className="font-['Space_Grotesk'] font-bold tracking-tight text-white">{t("ezerp", "EzERP")}<span className="text-teal-500">{t('preview.secure')}</span></span>
                    </div>
                    <div className="flex flex-col">
                        <h1 className="text-sm font-semibold text-white tracking-tight truncate max-w-[300px]">{material.title}</h1>
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] text-slate-500 uppercase tracking-widest font-black">{t('preview.revision')}: {material.updated_at || '2026.03.01'}</span>
                            <div className="h-1 w-1 rounded-full bg-teal-500/40" />
                            <span className="text-[9px] text-teal-500/80 font-mono">{t('preview.status_verified')}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/5" onClick={() => handleAction('Printing')}>
                            <Printer className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/5" onClick={() => handleAction('Sharing')}>
                            <Share2 className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="w-[1px] h-6 bg-white/10 mx-1" />
                    <Button 
                        className="bg-white hover:bg-slate-100 text-black font-bold gap-2 h-9 px-4 rounded-lg shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all" 
                        onClick={handleDownload}
                    >
                        <Download className="h-4 w-4" />
                        {t('preview.download')}
                    </Button>
                </div>
            </header>

            {/* Main Viewer Architecture */}
            <main className="flex-1 flex overflow-hidden">
                {/* Lateral Sidebar (Metadata) */}
                <aside className="w-80 border-r border-white/5 bg-[#0A0A0A]/40 overflow-y-auto hidden lg:block">
                    <div className="p-6 space-y-8">
                        <section className="space-y-4">
                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t('preview.doc_genesis')}</h3>
                            <Card className="bg-[#111] border-white/5 shadow-inner">
                                <CardContent className="p-5 space-y-4 font-mono text-[11px]">
                                    <div className="flex justify-between items-center group">
                                        <span className="text-slate-500 group-hover:text-slate-400 transition-colors uppercase">{t('preview.format')}</span>
                                        <span className="text-teal-400 font-bold px-2 py-0.5 bg-teal-500/10 rounded tracking-tighter uppercase">{material.file_type}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 uppercase">{t('preview.category')}</span>
                                        <span className="text-white font-medium tracking-tight uppercase">{material.category}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 uppercase">{t('preview.owner')}</span>
                                        <span className="text-white font-medium tracking-tight truncate max-w-[120px]">{material.owner}</span>
                                    </div>
                                    <div className="flex flex-col gap-2 pt-2 border-t border-white/5">
                                        <span className="text-slate-500 uppercase">{t('preview.classification')}</span>
                                        <div className="flex flex-wrap gap-1.5">
                                            {material.tags?.map((tag) =>
                                              <span key={tag} className="text-[9px] bg-white/5 px-2 py-0.5 rounded-sm text-slate-300 border border-white/5 uppercase font-bold tracking-tight">{tag}</span>
                                            )}
                                            <span className="text-[9px] bg-teal-500/10 px-2 py-0.5 rounded-sm text-teal-500 border border-teal-500/20 uppercase font-black tracking-widest">{t('preview.secure_label')}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </section>

                        <section className="space-y-4">
                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t('preview.asset_context')}</h3>
                            <div className="relative p-4 rounded-xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 shadow-sm">
                                <Info className="h-4 w-4 text-teal-500/60 absolute -top-2 -right-2" />
                                <p className="text-sm text-slate-400 leading-relaxed font-light">
                                    {material.description || t('preview.no_context')}
                                </p>
                            </div>
                        </section>

                        <section className="pt-8 border-t border-white/5">
                            <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono italic opacity-60">
                                <ShieldCheck className="h-3 w-3 text-teal-600" />
                                {t('preview.encryption_active')}
                            </div>
                        </section>
                    </div>
                </aside>

                {/* Content Area */}
                <div className="flex-1 bg-[#080808] relative flex flex-col overflow-hidden">
                    {/* Viewer Controls */}
                    <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-[#1A1A1A]/90 backdrop-blur-2xl border border-white/10 px-5 py-2.5 rounded-2xl flex items-center gap-6 z-20 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.8)] ring-1 ring-white/5">
                        <div className="flex flex-col">
                            <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.3em] text-center leading-none mb-1">{t('preview.slide')}</span>
                            <span className="text-xs font-black text-white tracking-widest text-center">{currentPage} <span className="text-white/20">/</span> {totalPages}</span>
                        </div>
                        <div className="w-[1px] h-6 bg-white/10" />
                        <div className="flex items-center gap-1.5">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                            >
                                <ChevronLeft className="h-5 w-5 rotate-180" />
                            </Button>
                        </div>
                        <div className="w-[1px] h-6 bg-white/10" />
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-all">
                            <Maximize2 className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Rendering Engine */}
                    <div className="flex-1 overflow-auto p-12 lg:p-24 flex justify-center items-start bg-[radial-gradient(circle_at_center,_#111_0%,_#050505_100%)]">
                        <div className={cn(
                          "w-full max-w-6xl rounded shadow-[0_60px_100px_-20px_rgba(0,0,0,0.9)] flex flex-col relative overflow-hidden ring-1 ring-white/10 transition-all duration-500",
                          isPowerPoint ? "aspect-video" : "aspect-[1/1.414]"
                        )}>
                            {/* Watermark Pattern */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] rotate-[-25deg] pointer-events-none select-none z-0">
                                <span className="text-[12rem] font-black font-['Space_Grotesk'] text-white whitespace-nowrap tracking-tighter uppercase">{t("ezerpConfidentialSecure", "EzERP CONFIDENTIAL \u2022 SECURE VAULT")}</span>
                            </div>

                            <div className="relative z-10 w-full h-full flex flex-col transition-all">
                                {isPresentation && currentPage === 4 ?
                                    <PowerPointSlide material={material} /> :

                                    <div className="bg-white w-full h-full p-16 flex flex-col overflow-hidden relative animate-fade-in">
                                        <div className="flex justify-between items-start relative z-10">
                                            <div className="space-y-4">
                                                <div className="h-10 w-48 bg-teal-600 rounded-lg shadow-sm" />
                                                <div className="h-1 w-32 bg-slate-100 rounded" />
                                            </div>
                                            <div className="text-right space-y-2">
                                                <p className="text-[10px] font-black text-slate-900 uppercase tracking-[0.3em] leading-none">{t('preview.internal_use')}</p>
                                                <div className="flex items-center justify-end gap-2 text-[9px] text-slate-400 font-mono uppercase font-bold">
                                                    <span>{t('preview.ref_no')}: {material.id}</span>
                                                    <div className="h-1 w-1 bg-slate-300 rounded-full" />
                                                    <span>{t('preview.s_idx')}: {currentPage}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-20 space-y-8 relative z-10">
                                            <h2 className="text-6xl font-bold text-slate-900 font-['Space_Grotesk'] tracking-tighter leading-[0.9] max-w-3xl">
                                                {material.title}
                                            </h2>
                                            <div className="h-[6px] w-24 bg-teal-500 rounded-full" />
                                        </div>

                                        {material.content ? (
                                            <div className="mt-8 flex-1 overflow-y-auto text-slate-800 text-sm font-sans pr-2 leading-relaxed border border-slate-100 rounded-lg p-6 bg-slate-50/30 whitespace-pre-wrap max-h-[60vh] text-left">
                                                {material.content}
                                            </div>
                                        ) : (
                                            <div className="mt-16 grid grid-cols-2 gap-20 relative z-10">
                                                <div className="space-y-6 pt-4">
                                                    <div className="h-4 w-full bg-slate-50 rounded" />
                                                    <div className="h-4 w-full bg-slate-50 rounded" />
                                                    <div className="h-4 w-3/4 bg-slate-50 rounded" />
                                                    <div className="h-4 w-full bg-slate-50 rounded" />
                                                    <div className="h-4 w-5/6 bg-slate-50 rounded" />
                                                </div>
                                                <div className="bg-slate-50/50 rounded-3xl p-12 flex flex-col items-center justify-center text-center gap-8 border border-slate-100 shadow-sm transition-transform hover:scale-[1.01]">
                                                    <div className="bg-white p-6 rounded-[2.5rem] shadow-2xl text-teal-600 ring-4 ring-slate-50">
                                                        {isPowerPoint ? <FileIcon className="h-12 w-12" /> : <FileText className="h-12 w-12" />}
                                                    </div>
                                                    <div className="space-y-3">
                                                        <p className="text-[12px] font-black text-slate-900 uppercase tracking-[0.2em]">{material.file_type}{t("securedObject", "Secured Object")}</p>
                                                        <p className="text-xs text-slate-400 leading-relaxed max-w-[200px] mx-auto">{t("verifiedCryptographicallyBy", "Verified cryptographically by EzERP Integrity Engine. No threats detected.")}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Link Simulation if real URL exists */}
                                        {material.file_url && !material.file_url.startsWith('#') &&
                                            <div className="mt-auto pt-16 flex justify-center">
                                                <Button
                                                    variant="outline"
                                                    className="border-teal-100 text-teal-600 gap-2 hover:bg-teal-50 rounded-2xl px-10 h-12 font-bold shadow-sm"
                                                    onClick={handleDownload}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    {t('preview.authorized_access')}
                                                </Button>
                                            </div>
                                        }

                                        <div className="mt-auto pt-12 flex justify-between items-end border-t border-slate-100">
                                            <div className="text-[10px] font-mono text-slate-300 uppercase tracking-widest font-bold">{t('preview.ecosystem')}</div>
                                            <div className="text-[10px] font-mono text-slate-300 uppercase tracking-widest font-black">{t('preview.page_of', { count: currentPage, total: totalPages })}</div>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Status Footer */}
            <footer className="h-10 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between px-6 text-[9px] font-mono text-slate-500 uppercase tracking-[0.2em] relative z-50">
                <div className="flex items-center gap-8">
                    <span className="flex items-center gap-2 overflow-hidden">
                        <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse shrink-0 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                        <span className="font-bold text-slate-400">{t('preview.session_active')}</span>
                    </span>
                    <span className="hidden sm:inline opacity-50">{t("clusterkey", "Cluster-Key:")}{Math.random().toString(36).substring(7).toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="hidden md:inline">{t('preview.encryption_enabled')}</span>
                    <ShieldCheck className="h-3 w-3 text-teal-500" />
                </div>
            </footer>
        </div>
  );
};